import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { retryUnlessNotFound } from "@/lib/api/client";
import {
  getDataSources,
  getDataSource,
  getDataSourceSnapshot,
  createDataSource,
  updateDataSource,
  deleteDataSource,
  previewDataSource,
} from "@/lib/api/data-sources";

export const useDataSourcesQuery = (
  options?: { staleTime?: number }
) =>
  useQuery({
    queryKey: ["dataSources"],
    queryFn: getDataSources,
    ...options,
  });

export const useDataSourceQuery = (id: string) =>
  useQuery({
    queryKey: ["dataSource", id],
    queryFn: () => getDataSource(id).then((res) => res.data),
    enabled: !!id,
    retry: retryUnlessNotFound,
  });

export const useDataSourceSnapshotQuery = (
  dataSourceId: string,
  customerUserId: string
) =>
  useQuery({
    queryKey: ["dataSourceSnapshot", dataSourceId, customerUserId],
    queryFn: () =>
      getDataSourceSnapshot(dataSourceId, customerUserId).then((res) => res.data),
    enabled: !!dataSourceId && !!customerUserId,
    retry: retryUnlessNotFound,
  });

export const useCreateDataSourceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof createDataSource>[0]) =>
      createDataSource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataSources"] });
    },
  });
};

export const useUpdateDataSourceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateDataSource>[1] }) =>
      updateDataSource(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["dataSources"] });
      queryClient.invalidateQueries({ queryKey: ["dataSource", variables.id] });
    },
  });
};

export const useDeleteDataSourceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDataSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataSources"] });
    },
  });
};

export const usePreviewDataSourceMutation = () =>
  useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: {
      data: Parameters<typeof previewDataSource>[0];
      serviceId?: string;
    }) => previewDataSource(data, { serviceId }),
  });
